import { readCookie } from "@app/lib/utils";

export interface ApiError {
  code: string;
  message: string;
}

export class ApiRequestError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

let csrfMemo: string | null = null;

async function ensureCsrf(): Promise<string> {
  const cookie = readCookie("jc_csrf");
  if (cookie) {
    csrfMemo = cookie;
    return cookie;
  }
  if (csrfMemo) return csrfMemo;
  const res = await fetch("/api/auth/csrf", {
    method: "GET",
    credentials: "same-origin",
  });
  if (!res.ok) throw new ApiRequestError(res.status, "csrf_failed", "Could not initialize CSRF");
  const data = (await res.json()) as { token: string };
  csrfMemo = data.token;
  return data.token;
}

export interface ApiOpts {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  body?: unknown;
  /** include x-csrf-token header (defaults true for non-GET) */
  csrf?: boolean;
}

export async function api<T = unknown>(path: string, opts: ApiOpts = {}): Promise<T> {
  const method = opts.method ?? (opts.body !== undefined ? "POST" : "GET");
  const headers = new Headers({ accept: "application/json" });
  const wantsCsrf = opts.csrf ?? method !== "GET";
  if (wantsCsrf) headers.set("x-csrf-token", await ensureCsrf());
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers.set("content-type", "application/json");
    body = JSON.stringify(opts.body);
  }
  const res = await fetch(path, {
    method,
    headers,
    body,
    credentials: "same-origin",
  });
  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const e = (parsed as { error?: ApiError } | null)?.error;
    throw new ApiRequestError(res.status, e?.code ?? "http_error", e?.message ?? res.statusText);
  }
  return parsed as T;
}

export interface MeResponse {
  id: string;
  email: string;
}

export function getMe(): Promise<MeResponse> {
  return api<MeResponse>("/api/auth/me");
}

export function login(email: string, password: string): Promise<MeResponse> {
  return api<MeResponse>("/api/auth/login", { method: "POST", body: { email, password } });
}

export function register(email: string, password: string, invitationCode: string): Promise<MeResponse> {
  return api<MeResponse>("/api/auth/register", { method: "POST", body: { email, password, invitationCode } });
}

export function logout(): Promise<{ ok: true }> {
  return api<{ ok: true }>("/api/auth/logout", { method: "POST" });
}

export function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ ok: true }> {
  return api<{ ok: true }>("/api/auth/change-password", {
    method: "POST",
    body: { currentPassword, newPassword },
  });
}

// Streaming-friendly proxy to the Hermes gateway on the user's VM. Returns
// the raw Response so callers can pipe the body (SSE / chunked) directly.
export async function agentChat(
  body: unknown,
  opts: { signal?: AbortSignal } = {},
): Promise<Response> {
  const headers = new Headers({
    "content-type": "application/json",
    accept: "application/json, text/event-stream",
    "x-csrf-token": await ensureCsrf(),
  });
  return fetch("/api/agent/chat", {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    credentials: "same-origin",
    signal: opts.signal,
  });
}

// Agent ---------------------------------------------------------------------

export type AgentStatus =
  | "none"
  | "provision_requested"
  | "creating"
  | "awaiting_phone_home"
  | "running"
  | "stopped"
  | "error";

export interface AgentStatusResponse {
  status: AgentStatus;
  lastPhoneHomeAt: string | null;
  serverType: string | null;
  region: string | null;
  publicIp: string | null;
}

export function getAgentStatus(): Promise<AgentStatusResponse> {
  return api<AgentStatusResponse>("/api/agent/status");
}

export function createAgent(): Promise<{ status: string }> {
  return api("/api/agent/create", { method: "POST", body: {} });
}

export function startAgent(): Promise<{ status: string }> {
  return api("/api/agent/start", { method: "POST", body: {} });
}

export function stopAgent(): Promise<{ status: string }> {
  return api("/api/agent/stop", { method: "POST", body: {} });
}

export function rebuildAgent(newType: string): Promise<{ status: string; serverType: string }> {
  return api("/api/agent/rebuild", { method: "POST", body: { newType } });
}

export function deleteAgent(): Promise<{ status: string }> {
  return api("/api/agent/delete", { method: "POST", body: {} });
}

export interface AgentSettings {
  personaMd: string;
  modelMap: { chat: string; cheap: string; image: string };
  timezone: string;
  responseStyle: "balanced" | "concise" | "detailed";
  freeModel: boolean;
  updatedAt?: string;
}

export function getAgentSettings(): Promise<AgentSettings> {
  return api<AgentSettings>("/api/agent/settings");
}

export function saveAgentSettings(s: AgentSettings): Promise<{ ok: true }> {
  return api("/api/agent/settings", { method: "POST", body: s });
}

// OpenRouter ----------------------------------------------------------------

export interface SpendResponse {
  usageCents: number;
  limitCents: number;
  remainingCents: number;
  ts: number;
  source: "cache" | "live";
}

export function getSpend(): Promise<SpendResponse> {
  return api<SpendResponse>("/api/openrouter/spend");
}

export function provisionOpenRouter(): Promise<{ keyId: string; capRealUsdCents: number }> {
  return api("/api/openrouter/provision", { method: "POST", body: {} });
}

export function setFreeModel(enabled: boolean): Promise<{ ok: true; freeModel: boolean }> {
  return api("/api/openrouter/free-toggle", { method: "POST", body: { enabled } });
}

// Telegram -------------------------------------------------------------------

export type TelegramBotStatus =
  | { configured: false }
  | {
      configured: true;
      username: string;
      botId: number;
      telegramAllowedUsers: string | null;
    };

export function getTelegramBot(): Promise<TelegramBotStatus> {
  return api<TelegramBotStatus>("/api/telegram/bot");
}

export function saveTelegramBot(opts: {
  token: string;
  telegramAllowedUsers?: string;
}): Promise<{ ok: true; username: string; botId: number }> {
  return api("/api/telegram/bot", { method: "POST", body: opts });
}

export function patchTelegramBot(telegramAllowedUsers: string | null): Promise<{ ok: true }> {
  return api("/api/telegram/bot", { method: "PATCH", body: { telegramAllowedUsers } });
}

export function deleteTelegramBot(): Promise<{ ok: true }> {
  return api("/api/telegram/bot", { method: "DELETE" });
}

export function applyConfig(): Promise<{ status: string }> {
  return api("/api/agent/apply-config", { method: "POST", body: {} });
}

export interface OrModelOption {
  id: string;
  name?: string;
  context_length?: number;
}

export interface ModelsResponse {
  data: OrModelOption[];
  ts: number;
  source: "cache" | "live";
}

export function getModels(opts?: { image?: boolean }): Promise<ModelsResponse> {
  const path = opts?.image
    ? "/api/openrouter/models?image=true"
    : "/api/openrouter/models";
  return api<ModelsResponse>(path);
}
